import React from 'react';
import { X, Plus, Minus, ShoppingBag, ShieldCheck, Truck } from 'lucide-react';

const CartDrawer = ({
  open,
  onClose,
  items,
  onUpdateQuantity,
  onUpdateColor,
  subtotal,
  onCheckout,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute top-0 right-0 w-full sm:w-[26rem] h-full bg-surface shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-line">
          <h2 className="font-display text-2xl text-ink">Your Bag</h2>
          <button onClick={onClose} className="p-3 hover:bg-paper rounded-full transition-colors" aria-label="Close cart">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5" data-lenis-prevent>
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-ink-faint">
              <ShoppingBag className="w-10 h-10" />
              <p>Your bag is empty.</p>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li key={item.cartLineId} className="flex gap-4">
                  <div className="w-20 h-24 flex-shrink-0 bg-paper rounded-sm overflow-hidden">
                    <img
                      src={item.imageUrl || item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/images/placeholder.jpg'; }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink font-medium leading-snug mb-1">{item.name}</p>
                    {item.selectedColor && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="w-3 h-3 rounded-full border border-line" style={{ background: item.selectedColorCode }} />
                        <span className="text-xs text-ink-faint">{item.colorName || item.selectedColor}</span>
                      </div>
                    )}
                    {Array.isArray(item.availableColors) && item.availableColors.length > 1 && (
                      <div className="mb-2">
                        <p className="text-[11px] text-ink-faint mb-1">Color — select before checkout:</p>
                        <div className="flex items-center gap-2">
                        {item.availableColors.slice(0, 5).map((c) => (
                          <button
                            key={c.id}
                            onClick={() => onUpdateColor(item.cartLineId, c)}
                            className={`w-6 h-6 rounded-full border-2 ${item.selectedColorCode === c.colorCode ? 'border-ink' : 'border-line'}`}
                            style={{ background: c.colorCode }}
                            aria-label={c.color}
                            aria-pressed={item.selectedColorCode === c.colorCode}
                            title={c.color}
                          />
                        ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 border border-line rounded-full px-1">
                        <button onClick={() => onUpdateQuantity(item.cartLineId, item.quantity - 1)} className="p-1.5 hover:text-accent" aria-label="Decrease quantity">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm w-4 text-center">{item.quantity}</span>
                        <button onClick={() => onUpdateQuantity(item.cartLineId, item.quantity + 1)} className="p-1.5 hover:text-accent" aria-label="Increase quantity">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-sm text-ink font-medium">KSh {(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-line px-6 py-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-soft">Subtotal</span>
              <span className="text-ink">KSh {subtotal.toLocaleString()}</span>
            </div>
            <p className="text-xs text-ink-faint">
              Shipping and any additional costs are calculated at checkout, based on your delivery location.
            </p>
            <button
              onClick={onCheckout}
              className="w-full bg-ink text-paper dpc-label py-3.5 rounded-full hover:bg-accent transition-colors mt-2"
            >
              Proceed to checkout
            </button>
            <div className="flex items-center justify-center gap-5 pt-1 text-ink-faint">
              <span className="flex items-center gap-1.5 text-xs"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment</span>
              <span className="flex items-center gap-1.5 text-xs"><Truck className="w-3.5 h-3.5" /> Nationwide delivery</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
